import { Router, ScreenBase } from "@frui.ts/screens";
import ChargeOrderCustomerRatingRequest, { ChargeOrderCustomerRatingRules } from "entities/chargeOrderCustomerRatingRequest";
import CustomerRatingScore from "entities/customerRatingScore";
import { action, computed, observable, reaction } from "mobx";
import LocalizationService from "services/localizationService";
import ChargeOrderRepository from "repositories/chargeOrderRepository";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import { getValidationMessage, validate } from "@frui.ts/validation";
import EventBus, { customerRatingOrderData } from "services/eventBus";
import EshopError from "models/eshopError";
import { scrollToElement } from "utils/scrollTo";
import CustomerReviewStorageRepository from "repositories/customerReviewStorageRepository/customerReviewStorageRepository";
import CustomerRating from "entities/customerRating";
import { isMobile } from "react-device-detect";

export const CustomerReviewModuleNavigationName = "customer_review";

export interface CustomerReview {
  id: string;
  value: CustomerRatingScore;
}

export const customerReview: CustomerReview[] = [
  { id: "1", value: CustomerRatingScore.Positive },
  { id: "2", value: CustomerRatingScore.Neutral },
  { id: "3", value: CustomerRatingScore.Negative },
];

@Router.registerRoute({ route: CustomerReviewModuleNavigationName, name: "customer_review" })
export default class CustomerReviewViewModel extends ScreenBase {
  unsubscriptions: Array<() => void> = [];
  @observable customerRating: ChargeOrderCustomerRatingRequest;
  @observable orderId: string;
  @observable step = 1;
  @observable errorMessage: string;

  constructor(
    protected eventBus: EventBus,
    private repository: ChargeOrderRepository,
    private customerReviewStorage: CustomerReviewStorageRepository,
    private localizationService: LocalizationService
  ) {
    super();
    this.customerRating = new ChargeOrderCustomerRatingRequest();
    attachAutomaticBlurValidator(this.customerRating, {
      ...ChargeOrderCustomerRatingRules,
    });
    this.unsubscriptions.push(this.eventBus.subscribe(customerRatingOrderData, this.onCustomerRatingOrderData));
  }

  protected onInitialize(): Promise<any> | void {
    return super.onInitialize();
  }

  protected onActivate() {
    this.checkCustomerRatingStorage();
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach((unsubscribe) => unsubscribe());
      this.unsubscriptions = [];
    }
    this.customerReviewStorage.removeCustomerRating(this.orderId);
    return super.onDeactivate(isClosing);
  }

  @computed
  get customerReviews() {
    return customerReview.map((item) => item);
  }

  @computed
  get rating() {
    return this.customerRating;
  }

  @computed
  get ratingScore() {
    return this.rating.score;
  }

  @computed
  get successRatingCardElement() {
    return window.document.getElementById("success-rating-card");
  }

  @computed
  get error() {
    return getValidationMessage(this.rating, "score");
  }

  @action.bound
  public customerReviewColor(value: CustomerRatingScore) {
    switch (value) {
      case CustomerRatingScore.Neutral:
        return "color-alert";
      case CustomerRatingScore.Negative:
        return "color-danger";
      default:
        return "color-success";
    }
  }

  @action.bound
  public checkCustomerRatingStorage() {
    const storageRating = this.customerReviewStorage.getCustomerRating(this.orderId);
    if (!storageRating) {
      return;
    }
    if (storageRating.step) {
      this.step = storageRating.step;
    }
    if (storageRating.warning) {
      this.errorMessage = storageRating.warning;
    }
    if (storageRating.rating) {
      this.customerRating = storageRating.rating;
    }
  }

  @computed
  get isDisabled() {
    if (!!this.errorMessage && this.errorMessage !== "REQUEST_MODEL_VALIDATION_FAILED") {
      return true;
    }
    if (!!this.errorMessage) {
      return true;
    }
    return false;
  }

  @action.bound
  isDisabledConfirm() {
    if (!validate(this.rating)) {
      return true;
    }
    return false;
  }

  @action.bound
  public async onSubmit() {
    if (validate(this.rating) && this.orderId) {
      const response = await this.repository.postCustomerRating(this.orderId, this.rating);
      if (response instanceof EshopError && response.title && response.type) {
        this.errorMessage = response.type;
        const storageRating: CustomerRating = {
          step: this.step,
          warning: this.errorMessage,
          rating: this.rating,
          orderId: this.orderId,
        };
        this.customerReviewStorage.addCustomerRating(storageRating);
        return;
      }
      this.step = 2;
      const storageRating: CustomerRating = {
        step: this.step,
        warning: this.errorMessage,
        rating: this.rating,
        orderId: this.orderId,
      };
      this.customerReviewStorage.addCustomerRating(storageRating);
      if (isMobile && this.successRatingCardElement) {
        scrollToElement(this.successRatingCardElement);
      }
    }
  }

  @action.bound
  public async onCustomerRatingOrderData(data: ReturnType<typeof customerRatingOrderData>) {
    const { orderId } = data.payload;

    if (orderId) {
      this.orderId = orderId;
    }
  }

  @action.bound
  translate(code: string, disableUnbreakableRules = false) {
    return this.localizationService.translateGeneral(`customerRating.${code}`, undefined, disableUnbreakableRules);
  }

  @action.bound
  translateError(code: string, disableUnbreakableRules = false) {
    return this.localizationService.translateGeneral(`customerRating.errors.${code}`, undefined, disableUnbreakableRules);
  }
}
